import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, TrendingUp, Film, Shield, Loader2, Search, UserCog, LogOut, Download, RefreshCw, Trash2, Coins, LifeBuoy, Activity } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import AdminAnalytics from "../components/AdminAnalytics";
import ReferralAnalytics from "../components/ReferralAnalytics";
import { AdminPricingTable } from "../components/AdminPricingTable";
import { PaymentReviewQueue } from "../components/PaymentReviewQueue";
import { PricingMigrationAdmin } from "../components/PricingMigrationAdmin";

function TrendPill({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
        up ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"
      }`}
    >
      {up ? "▲" : "▼"} {Math.abs(pct)}%
    </span>
  );
}

function StatCard({ label, value, hint, icon: Icon, trendPct }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 p-6" data-testid={`stat-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {Icon ? <Icon className="w-3 h-3" /> : null} {label}
        </div>
        <TrendPill pct={trendPct} />
      </div>
      <div className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-white">
        {value}
      </div>
      {hint && <div className="text-[11px] font-mono text-zinc-500 mt-2">{hint}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [impActing, setImpActing] = useState(null);
  const [range, setRange] = useState(30);
  const [rangeLoading, setRangeLoading] = useState(false);

  // Single effect keyed on [navigate, range]: fetches stats every time range
  // changes; users are fetched once on mount via a ref-guarded first-load flag.
  const firstLoadRef = useRef(true);
  useEffect(() => {
    (async () => {
      const isFirst = firstLoadRef.current;
      if (!isFirst) setRangeLoading(true);
      try {
        const statsPromise = api.get(`/admin/stats?days=${range}`);
        if (isFirst) {
          const [statsRes, usersRes] = await Promise.all([
            statsPromise,
            api.get("/admin/users?limit=50"),
          ]);
          setStats(statsRes.data);
          setUsers(usersRes.data.users || []);
        } else {
          const { data } = await statsPromise;
          setStats(data);
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          toast.error("Admins only");
          navigate("/dashboard");
        } else {
          if (process.env.NODE_ENV !== "production") console.warn(err?.message);
        }
      } finally {
        if (isFirst) {
          setLoading(false);
          firstLoadRef.current = false;
        } else {
          setRangeLoading(false);
        }
      }
    })();
  }, [navigate, range]);

  const search = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}&limit=50`);
      setUsers(data.users || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn(err?.message);
    }
  };

  const impersonate = async (targetId, email) => {
    setImpActing(targetId);
    try {
      await api.post("/admin/impersonate", { user_id: targetId });
      toast.success(`Impersonating ${email}`);
      await refresh?.();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impersonation failed");
    } finally {
      setImpActing(null);
    }
  };

  const [jobActing, setJobActing] = useState(null);

  const downloadJob = async (jobId, fmt) => {
    try {
      const resp = await api.get(`/admin/jobs/${jobId}/download/${fmt}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${jobId}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    }
  };

  const regenerateJob = async (jobId) => {
    if (!window.confirm("Re-run the LinguaSub engine on this job? Existing subs will be replaced.")) return;
    setJobActing(`regen-${jobId}`);
    try {
      const { data } = await api.post(`/admin/jobs/${jobId}/regenerate`);
      toast.success(`Regenerated · ${data.segments} segments`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regenerate failed");
    } finally {
      setJobActing(null);
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm("Delete this job and its media? This cannot be undone.")) return;
    setJobActing(`del-${jobId}`);
    try {
      await api.delete(`/admin/jobs/${jobId}`);
      toast.success("Job deleted");
      setStats((s) => s ? {
        ...s,
        total_jobs: Math.max(0, (s.total_jobs || 1) - 1),
        recent_jobs: (s.recent_jobs || []).filter((j) => j.job_id !== jobId),
      } : s);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setJobActing(null);
    }
  };

  if (loading) {
    return (
      <div className="grain-bg min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  const planBreakdown = Object.entries(stats?.active_subs_by_plan || {});

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white" data-testid="admin-dashboard-page">
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-4 h-4 text-emerald-400" />
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Admin</div>
            <div className="font-heading text-lg font-bold tracking-tight">LinguaSub Control</div>
          </div>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <span className="font-mono">{user?.email}</span>
            <Link
              to="/admin/support"
              data-testid="admin-support-nav-link"
              className="border border-blue-500/30 text-blue-300 hover:bg-blue-500/10 px-3 py-1.5 text-xs uppercase font-mono tracking-widest"
            >
              <LifeBuoy className="w-3 h-3 inline mr-1" /> Support
            </Link>
            <Link
              to="/admin/spend"
              data-testid="admin-spend-nav-link"
              className="border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 px-3 py-1.5 text-xs uppercase font-mono tracking-widest"
            >
              <Coins className="w-3 h-3 inline mr-1" /> Spend info
            </Link>
            <Link
              to="/admin/diagnostics"
              data-testid="admin-diagnostics-nav-link"
              className="border border-zinc-700 text-zinc-300 hover:bg-zinc-800 px-3 py-1.5 text-xs uppercase font-mono tracking-widest"
            >
              <Activity className="w-3 h-3 inline mr-1" /> Diagnostics
            </Link>
            <Link to="/dashboard" className="border border-zinc-800 hover:bg-zinc-800 px-3 py-1.5 text-xs uppercase font-mono tracking-widest">
              <LogOut className="w-3 h-3 inline mr-1" /> Exit
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12 space-y-10">
        {/* Stats grid */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total users"
            value={stats?.total_users ?? 0}
            hint={stats?.signups_today != null ? `+${stats.signups_today} today · +${stats.signups_this_week ?? 0} this week` : undefined}
            trendPct={stats?.signups_wow_pct}
            icon={Users}
          />
          <StatCard
            label="Total jobs"
            value={stats?.total_jobs ?? 0}
            hint={stats?.jobs_today != null ? `+${stats.jobs_today} today` : undefined}
            icon={Film}
          />
          <StatCard
            label="Active paid subs"
            value={planBreakdown.filter(([k]) => k !== "free" && k !== "admin").reduce((s, [, v]) => s + v, 0)}
            hint={
              Object.keys(stats?.plan_mix_pct || {})
                .filter((k) => k !== "free" && k !== "admin")
                .map((k) => `${k} ${stats.plan_mix_pct[k]}%`)
                .join(" · ") || "no paid subs"
            }
            icon={Shield}
          />
          <StatCard
            label="Active pack value (INR)"
            value={`\u20B9${(stats?.mrr_usd ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
            hint="from currently active subscriptions"
            trendPct={stats?.mrr_wow_pct}
            icon={TrendingUp}
          />
        </section>

        <AdminPricingTable />
        {stats && <PricingMigrationAdmin />}
          <PaymentReviewQueue />

        {/* Top target languages */}
        {stats?.top_target_languages?.length > 0 && (
          <section
            data-testid="top-target-languages"
            className="border border-zinc-800 bg-zinc-900/40 p-6"
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-4">
              Top target languages (all-time)
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.top_target_languages.map((row) => (
                <div
                  key={row.language}
                  className="border border-zinc-800 bg-zinc-950/60 px-3 py-2"
                >
                  <div className="text-sm text-white capitalize font-medium">
                    {row.language}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                    {row.jobs.toLocaleString()} jobs
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Range picker + 30-day analytics */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Trends</h2>
            <div
              className="inline-flex gap-px bg-zinc-800 border border-zinc-800"
              data-testid="analytics-range-picker"
            >
              {[
                { k: 7, label: "7d" },
                { k: 30, label: "30d" },
                { k: 90, label: "90d" },
                { k: 365, label: "365d" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setRange(opt.k)}
                  disabled={rangeLoading}
                  data-testid={`range-${opt.k}d`}
                  className={`h-8 px-3 text-[10px] font-mono uppercase tracking-widest transition-colors ${
                    range === opt.k
                      ? "bg-white text-zinc-950"
                      : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                  } ${rangeLoading ? "opacity-60 cursor-wait" : ""}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <AdminAnalytics
            signups={stats?.signups_series || stats?.signups_30d}
            mrr={stats?.mrr_series || stats?.mrr_30d}
            days={stats?.series_days || range}
          />
        </section>

        {/* Referral program analytics — admin-only */}
        <ReferralAnalytics />

        {/* Users table */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Users</h2>
            <form onSubmit={search} className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="email or name"
                  data-testid="admin-users-search"
                  className="h-9 pl-8 pr-3 bg-zinc-950 border border-zinc-800 text-sm font-mono w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </form>
          </div>
          <div className="border border-zinc-800 overflow-hidden" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Plan</th>
                  <th className="text-left px-4 py-3">Joined</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                    <td className="px-4 py-3 font-mono text-xs text-white">{u.email}</td>
                    <td className="px-4 py-3 text-zinc-400">{u.name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${
                        u.plan_key === "admin" ? "border-emerald-500/40 text-emerald-300"
                        : u.plan_key === "free" ? "border-zinc-700 text-zinc-500"
                        : "border-blue-500/40 text-blue-300"
                      }`}>{u.plan_key}</span>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => impersonate(u.user_id, u.email)}
                        disabled={u.plan_key === "admin" || impActing === u.user_id}
                        data-testid={`impersonate-${u.user_id}`}
                        className="text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:border-emerald-500/50 hover:text-emerald-300 px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      >
                        <UserCog className="w-3 h-3" /> {impActing === u.user_id ? "..." : "Act as"}
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-zinc-500 py-6 text-sm">No users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Recent jobs */}
        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-4">Recent jobs</h2>
          <div className="border border-zinc-800 overflow-hidden" data-testid="admin-recent-jobs">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3">Job</th>
                  <th className="text-left px-4 py-3">Direction</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">File</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent_jobs || []).map((j) => (
                  <tr key={j.job_id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400">{j.job_id.slice(0, 12)}</td>
                    <td className="px-4 py-3 text-zinc-300 text-xs">
                      {j.source_language} → {j.target_language}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className="border border-zinc-800 text-zinc-400 px-1.5 py-0.5 font-mono uppercase tracking-widest text-[10px]">
                        {j.input_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs truncate max-w-[220px]">{j.filename || "—"}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">
                      {j.created_at ? new Date(j.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => downloadJob(j.job_id, "srt")}
                          data-testid={`job-download-${j.job_id}`}
                          title="Download SRT"
                          className="text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-blue-500/50 hover:text-blue-300 px-2 py-1 inline-flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" /> SRT
                        </button>
                        <button
                          type="button"
                          onClick={() => regenerateJob(j.job_id)}
                          disabled={jobActing === `regen-${j.job_id}`}
                          data-testid={`job-regenerate-${j.job_id}`}
                          title="Re-run LinguaSub engine on stored source"
                          className="text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-emerald-500/50 hover:text-emerald-300 px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {jobActing === `regen-${j.job_id}`
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <RefreshCw className="w-3 h-3" />}
                          Regen
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteJob(j.job_id)}
                          disabled={jobActing === `del-${j.job_id}`}
                          data-testid={`job-delete-${j.job_id}`}
                          title="Delete job"
                          className="text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-red-500/50 hover:text-red-300 px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {jobActing === `del-${j.job_id}`
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Trash2 className="w-3 h-3" />}
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!stats?.recent_jobs || stats.recent_jobs.length === 0) && (
                  <tr><td colSpan={6} className="text-center text-zinc-500 py-6 text-sm">No jobs yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
